/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "./Navbar";
import Header from "./Header";
import baseUrl from "./BaseUrl";
import UserSegmentToggle from "./common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../context/UserSegmentContext";
import ExportButton from "./active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "./active-users/DateRangeBar";
import EmptyState from "./active-users/states/EmptyState";
import ErrorState from "./active-users/states/ErrorState";
import TableSkeleton from "./active-users/states/TableSkeleton";
import ActivityLogSummaryCards from "./activity-logs/ActivityLogSummaryCards";
import ActivityLogTrendChart from "./activity-logs/ActivityLogTrendChart";
import TopNamesPanel from "./activity-logs/TopNamesPanel";
import ReportInclusionPanel from "./activity-logs/ReportInclusionPanel";
import { dateFormatter, formatLastActive } from "../utils/utils";
import { readSavedFilters, usePersistFilters } from "../hooks/filterPersistence";
import "./active-users/active-users.css";
import "./activity-logs/activity-logs.css";

const LIMIT = 20;
const STATE_KEY = "reportHistoryState";
// True/False/- for the INT 0/1 flags (NULL → "-").
const flag = (v) => (v === 1 ? 'True' : v === 0 ? 'False' : '-');
const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'platform', label: 'Platform' },
  { key: 'mobile_app_version', label: 'Mobile Version' },
  { key: 'watch_app_version', label: 'Watch Version' },
  { key: 'report_name', label: 'Report Name' },
  { key: 'alert', label: 'Alert' },
  { key: 'pause', label: 'Pause' },
  { key: 'full_day_graph', label: 'Full Day Graph' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
  { key: 'created_at', label: 'Datetime' },
  { key: 'last_active', label: 'Last Active (UTC)' },
];

const ReportHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pinnedUserId = location.state?.user_id || null;
  const { segment } = useUserSegment();
  const userSegment = watchFilterValue(segment) || 'all';

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [uniqueUserCount, setUniqueUserCount] = useState(0);
  const [subscribedUserCount, setSubscribedUserCount] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [summary, setSummary] = useState({ logs: {}, users: {} });
  const [trend, setTrend] = useState([]);
  const [categorical, setCategorical] = useState({ logs: 0, topNames: [], inclusions: {} });
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [order, setOrder] = useState('DESC');

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore the view on return from a drill-down (Back), else default Last 30.
    const saved = readSavedFilters(STATE_KEY);
    if (saved) {
      setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
      setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
      setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
      setPreset(saved.preset || null);
      setSearchValue(saved.searchValue || '');
      setSubscriptionFilter(saved.subscriptionFilter || '');
      setOrder(saved.order || 'DESC');
      setPage(saved.page || 1);
    } else {
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => { if (token) loadAll(page, fromDate, toDate, searchValue, order, subscriptionFilter); }, [token]);

  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token || pinnedUserId) return;
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, order, subscriptionFilter);
  }, [segment]);

  // Snapshot filters so the exact view is restored on Back (remount).
  usePersistFilters(STATE_KEY, { fromDate, toDate, preset, searchValue, subscriptionFilter, order, page });

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const pin = pinnedUserId ? `&user_id=${pinnedUserId}` : '';
  const listQs = (p, f, t, s, o, sub) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&order=${o}&subscription_status=${sub}&user_segment=${userSegment}${pin}`;
  const rangeQs = (f, t, s, sub) =>
    `from_date=${f}&to_date=${t}&search=${s}&subscription_status=${sub}&user_segment=${userSegment}${pin}`;

  const applyList = (d) => {
    setRows(d.data || []);
    setTotalItem(d.totalItem || 0);
    setUniqueUserCount(d.uniqueUserCount || 0);
    setSubscribedUserCount(d.subscribedUserCount || 0);
    setTotalUserBase(d.totalUserCount || 0);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, s, o, sub) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/report-history?${listQs(p, f, t, s, o, sub)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, o, sub) => {
    setLoading(true); setError('');
    const range = rangeQs(f, t, s, sub);
    try {
      const [list, sum, tr, cat] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/report-history?${listQs(p, f, t, s, o, sub)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/report-history-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/report-history-trend?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/report-history-categorical?${range}`, auth()),
      ]);
      applyList(list.data);
      setSummary({ logs: sum?.data?.logs || {}, users: sum?.data?.users || {} });
      setTrend(Array.isArray(tr?.data?.data) ? tr.data.data : []);
      setCategorical({
        logs: cat?.data?.logs || 0,
        topNames: cat?.data?.topNames || [],
        inclusions: cat?.data?.inclusions || {},
      });
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (p) => { setPage(p); getList(p, fromDate, toDate, searchValue, order, subscriptionFilter); };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFromDate(fromStr ? new Date(fromStr) : null); setDisplayToDate(toStr ? new Date(toStr) : null);
    setPage(1); loadAll(1, fromStr, toStr, searchValue, order, subscriptionFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, order, subscriptionFilter); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setSubscriptionFilter(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', order, '');
  };
  const toggleSort = () => {
    const next = order === 'DESC' ? 'ASC' : 'DESC';
    setOrder(next); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, next, subscriptionFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/report-history-export?${rangeQs(fromDate, toDate, searchValue, subscriptionFilter)}&order=${order}`, auth());
    const data = res.data?.data || [];
    return data.map((r) => ({
      ...r,
      alert: flag(r.alert_sessions),
      pause: flag(r.pause_sessions),
      full_day_graph: flag(r.full_day_hr_graph),
      start_date: r.start_date ? dateFormatter(r.start_date) : '',
      end_date: r.end_date ? dateFormatter(r.end_date) : '',
      created_at: r.created_at ? dateFormatter(r.created_at) : '',
      last_active: r.last_active ? formatLastActive(r.last_active) : '',
    }));
  };

  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const logsVal = summary.logs?.current ?? totalItem;
  const usersVal = summary.users?.current ?? uniqueUserCount;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › <b>Report history</b></span>
            <span className="au-route">/report-history</span>
            <span className="au-spacer" />
            {!pinnedUserId && <UserSegmentToggle />}
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Report History</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the report history. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, order, subscriptionFilter)} />
          ) : (
            <>
              <ActivityLogSummaryCards
                logs={logsVal}
                users={usersVal}
                totalUserBase={totalUserBase}
                subscribedUserCount={subscribedUserCount}
                logsDelta={summary.logs?.delta}
                usersDelta={summary.users?.delta}
              />

              <ActivityLogTrendChart data={trend} title="Reports per day" seriesName="Reports" />

              <TopNamesPanel title="Report mix" items={categorical.topNames} logs={categorical.logs} />
              <ReportInclusionPanel inclusions={categorical.inclusions} logs={categorical.logs} />

              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search name, version, or report" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search report history" />
                    <select className="au-select" value={subscriptionFilter} onChange={(e) => setSubscriptionFilter(e.target.value)} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                  </>
                )}
                preset={preset} displayFrom={displayFromDate} displayTo={displayToDate} hasRange={hasRange}
                onPreset={handlePreset} onFromChange={handleFromChange} onToChange={handleToChange}
                onSearch={handleSearch} onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="al-range-note">
                The date range filters reports by their <b>record date (UTC)</b> — the value shown per row as “Datetime”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all reports (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No reports in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No reports match the current filters or segment.' : 'There is no report history to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Report history records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="report-history.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table al-table al-table--wide" data-testid="al-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Platform</th><th>Mobile Version</th><th>Watch Version</th>
                          <th>Report Name</th><th>Alert</th><th>Pause</th><th>Full Day Graph</th><th>Start Date</th><th>End Date</th>
                          <th className="au-th-sort" onClick={toggleSort} role="button" tabIndex={0}>
                            Datetime {order === 'DESC' ? '▼' : '▲'}
                          </th>
                          <th>Last Active (UTC)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.id}>
                            <td data-label="No">{(page - 1) * LIMIT + i + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{r.name}</td>
                            <td data-label="Platform">{r.platform}</td>
                            <td data-label="Mobile Version">{r.mobile_app_version}</td>
                            <td data-label="Watch Version">{r.watch_app_version}</td>
                            <td data-label="Report Name" className="au-ellipsis">{r.report_name}</td>
                            <td data-label="Alert">{flag(r.alert_sessions)}</td>
                            <td data-label="Pause">{flag(r.pause_sessions)}</td>
                            <td data-label="Full Day Graph">{flag(r.full_day_hr_graph)}</td>
                            <td data-label="Start Date">{r.start_date ? dateFormatter(r.start_date) : ''}</td>
                            <td data-label="End Date">{r.end_date ? dateFormatter(r.end_date) : ''}</td>
                            <td data-label="Datetime">{r.created_at ? dateFormatter(r.created_at) : ''}</td>
                            <td data-label="Last Active (UTC)">{r.last_active ? formatLastActive(r.last_active) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportHistory;
